import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft, ArrowRight, X, Play, CheckCircle, XCircle, Warning,
    Trophy, Books, Stethoscope, ChartLineUp, Target, Certificate,
    Clock, Lock, ArrowsClockwise, Sparkle, DownloadSimple, Lightbulb,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AcademyVisual, VISUALS_BY_KIND } from "@/pages/doctor/academyVisuals";

/* Academy Module Player — Full-screen kiosk mode
   -----------------------------------------------
   Rendered at /academy/module/:moduleId (outside /doctor so the portal
   sidebar + topbar are hidden). Provides an immersive Coursera-style
   learning experience:

     - TOP BAR: Exit → back to /doctor/academy/icpp | Course context
                | Overall module progress
     - LEFT RAIL: Sequential list of modules in the current phase.
                  Current is highlighted; earlier modules navigable;
                  later modules locked until the current one is passed.
     - MAIN AREA: Two screens driven by local state
         SLIDE mode → auto-generated slides from the module's existing
                      curriculum fields (objectives → info cards,
                      topics → step rows, plus intro + summary).
         QUIZ  mode → one question at a time. Answers collected locally
                      then batch-submitted (existing backend contract).
                      Score screen with pass/fail ring + inline
                      per-question review.

   iter100.104 — kills the ModuleDialog inside LearningAcademy.jsx in
   favour of this dedicated route.
*/


/* ─────────────────────────────────────────────────────────────────────
   Phase metadata (icon + subtitle) — mirrors LearningAcademy.jsx
   ───────────────────────────────────────────────────────────────────── */

const PHASE_META_BY_ID = {
    phase_1: { icon: Books, subtitle: "Foundation" },
    phase_2: { icon: Stethoscope, subtitle: "Clinical Execution" },
    phase_3: { icon: ChartLineUp, subtitle: "Practice Growth" },
    phase_4: { icon: Trophy, subtitle: "Mastery" },
};

const PASS_MARK = 0.7;


/* ─────────────────────────────────────────────────────────────────────
   Main component
   ───────────────────────────────────────────────────────────────────── */

export default function AcademyModulePlayer() {
    const { moduleId } = useParams();
    const navigate = useNavigate();

    const [content, setContent] = useState(null);
    const [progress, setProgress] = useState(null);
    const [loading, setLoading] = useState(true);

    // Slide state
    const [currentSlide, setCurrentSlide] = useState(0);

    // Screen mode: 'content' | 'quiz' | 'score'
    const [screen, setScreen] = useState("content");

    // Quiz state
    const [quizAnswers, setQuizAnswers] = useState({});          // { [qIndex]: optionIndex }
    const [quizCurrentIdx, setQuizCurrentIdx] = useState(0);
    const [quizResult, setQuizResult] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    // Load content + progress
    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [c, p] = await Promise.all([
                api.get("/academy/content"),
                api.get("/academy/progress"),
            ]);
            setContent(c.data);
            setProgress(p.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't load the module — please refresh.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // Reset slide on module change (deep-link)
    useEffect(() => {
        setCurrentSlide(0);
        setScreen("content");
        setQuizAnswers({});
        setQuizCurrentIdx(0);
        setQuizResult(null);
    }, [moduleId]);

    // Locate module + phase from content tree
    const moduleContext = useMemo(() => {
        if (!content?.phases) return null;
        for (const phase of content.phases) {
            for (const m of phase.modules || []) {
                if (m.id === moduleId) return { phase, module: m };
            }
        }
        return null;
    }, [content, moduleId]);

    const track = progress?.track;
    const module = moduleContext?.module;
    const phase = moduleContext?.phase;

    const trackData = module?.tracks?.[track] || {};
    const objectives = trackData.objectives || [];
    const topics = (trackData.topics || []).filter((t) => t.depth !== "none");
    const questions = module?.quiz?.[track] || [];
    const priorScore = module ? progress?.quiz_scores?.[module.id] : null;
    const viewed = module ? (progress?.viewed_modules || []).includes(module.id) : false;

    // Auto-generated slide list from module fields
    const slides = useMemo(() => {
        if (!module) return [];
        return buildSlidesFromModule({ module, phase, objectives, topics, questions });
    }, [module, phase, objectives, topics, questions]);

    // Exit → back to ICPP course view
    const handleExit = () => {
        navigate("/doctor/academy/icpp");
    };

    // Mark viewed helper — fired the first time the user reaches the last
    // slide OR starts the quiz, whichever comes first.
    const [viewedFired, setViewedFired] = useState(false);
    const maybeMarkViewed = useCallback(async () => {
        if (!module || viewed || viewedFired) return;
        setViewedFired(true);
        try {
            await api.post(`/academy/module/${module.id}/mark-viewed`);
            const p = await api.get("/academy/progress");
            setProgress(p.data);
        } catch (e) {
            // silent — non-blocking
        }
    }, [module, viewed, viewedFired]);

    // Slide navigation
    const goToSlide = (n) => {
        if (n < 0 || n >= slides.length) return;
        setCurrentSlide(n);
        if (n === slides.length - 1) maybeMarkViewed();
    };
    const nextSlide = () => goToSlide(currentSlide + 1);
    const prevSlide = () => goToSlide(currentSlide - 1);

    // Enter quiz
    const startQuiz = async () => {
        await maybeMarkViewed();
        setQuizAnswers({});
        setQuizCurrentIdx(0);
        setQuizResult(null);
        setScreen("quiz");
    };

    // Quiz navigation
    const selectQuizOption = (optionIdx) => {
        setQuizAnswers((prev) => ({ ...prev, [quizCurrentIdx]: optionIdx }));
    };
    const nextQuestion = () => {
        if (quizCurrentIdx < questions.length - 1) {
            setQuizCurrentIdx(quizCurrentIdx + 1);
        }
    };
    const prevQuestion = () => {
        if (quizCurrentIdx > 0) setQuizCurrentIdx(quizCurrentIdx - 1);
    };
    const submitQuiz = async () => {
        if (Object.keys(quizAnswers).length < questions.length) {
            toast.error(`Please answer all ${questions.length} questions before submitting.`);
            return;
        }
        setSubmitting(true);
        try {
            const arr = questions.map((_, i) => quizAnswers[i]);
            const r = await api.post(`/academy/quiz/${module.id}/submit`, { answers: arr });
            setQuizResult(r.data);
            // Refresh progress so left rail + top bar reflect the new state
            const p = await api.get("/academy/progress");
            setProgress(p.data);
            setScreen("score");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't submit quiz.");
        } finally {
            setSubmitting(false);
        }
    };

    const retakeQuiz = () => {
        setQuizAnswers({});
        setQuizCurrentIdx(0);
        setQuizResult(null);
        setScreen("quiz");
    };

    const reviewSlides = () => {
        setCurrentSlide(0);
        setScreen("content");
    };

    // Loading state
    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="flex items-center gap-3 text-muted-foreground text-sm">
                    <div className="w-4 h-4 rounded-full bg-primary/50 animate-pulse" />
                    Loading module…
                </div>
            </div>
        );
    }

    // Guard — no module found, or no track
    if (!moduleContext) {
        return (
            <PlayerError
                title="Module not found"
                message="We couldn't find this module in your Academy curriculum."
                onExit={handleExit}
            />
        );
    }
    if (!track) {
        return (
            <PlayerError
                title="Track not selected"
                message="Please select your track before opening a module."
                onExit={handleExit}
            />
        );
    }

    const PhaseIcon = PHASE_META_BY_ID[phase.id]?.icon || Books;
    const phaseModules = phase.modules || [];

    // Overall module progress % — content 0-70%, quiz +30% on pass
    const modulePct = (() => {
        if (priorScore?.passed) return 100;
        const contentPct = slides.length > 0
            ? Math.round(((currentSlide + 1) / slides.length) * 70)
            : 0;
        if (screen === "quiz") return 85;
        if (screen === "score" && !quizResult?.passed) return 90;
        return contentPct;
    })();

    return (
        <div className="fixed inset-0 z-50 bg-background flex flex-col" data-testid="academy-module-player">
            {/* TOP BAR */}
            <PlayerTopBar
                phase={phase}
                module={module}
                modulePct={modulePct}
                onExit={handleExit}
            />

            {/* BODY */}
            <div className="flex-1 flex overflow-hidden">
                {/* LEFT RAIL — module list */}
                <ModuleSidebar
                    phase={phase}
                    modules={phaseModules}
                    currentModuleId={module.id}
                    progress={progress}
                    PhaseIcon={PhaseIcon}
                    onSelect={(m) => {
                        if (m.id === module.id) return;
                        navigate(`/academy/module/${m.id}`);
                    }}
                />

                {/* MAIN AREA */}
                <div className="flex-1 flex flex-col overflow-hidden bg-secondary/20">
                    {screen === "content" && (
                        <ContentScreen
                            slides={slides}
                            currentSlide={currentSlide}
                            onGoTo={goToSlide}
                            onNext={nextSlide}
                            onPrev={prevSlide}
                            onStartQuiz={startQuiz}
                            hasQuiz={questions.length > 0}
                        />
                    )}
                    {screen === "quiz" && (
                        <QuizScreen
                            questions={questions}
                            currentIdx={quizCurrentIdx}
                            answers={quizAnswers}
                            onSelectOption={selectQuizOption}
                            onNext={nextQuestion}
                            onPrev={prevQuestion}
                            onSubmit={submitQuiz}
                            submitting={submitting}
                            onBackToContent={() => setScreen("content")}
                        />
                    )}
                    {screen === "score" && quizResult && (
                        <ScoreScreen
                            result={quizResult}
                            questions={questions}
                            answers={quizAnswers}
                            module={module}
                            phase={phase}
                            onRetake={retakeQuiz}
                            onReview={reviewSlides}
                            onNextModule={() => {
                                const currentIdx = phaseModules.findIndex((m) => m.id === module.id);
                                const next = phaseModules[currentIdx + 1];
                                if (next) navigate(`/academy/module/${next.id}`);
                                else navigate("/doctor/academy/icpp");
                            }}
                            hasNextModule={phaseModules.findIndex((m) => m.id === module.id) < phaseModules.length - 1}
                            onDownloadCert={async () => {
                                try {
                                    const resp = await api.get(`/academy/certificate/${phase.id}`, { responseType: "blob" });
                                    const url = URL.createObjectURL(resp.data);
                                    window.open(url, "_blank");
                                } catch (e) {
                                    toast.error(e?.response?.data?.detail || "Couldn't fetch certificate.");
                                }
                            }}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}


/* ─────────────────────────────────────────────────────────────────────
   TOP BAR
   ───────────────────────────────────────────────────────────────────── */

function PlayerTopBar({ phase, module, modulePct, onExit }) {
    const phaseSub = PHASE_META_BY_ID[phase.id]?.subtitle || phase.label;
    return (
        <div className="h-14 bg-white border-b border-border flex items-center justify-between px-4 md:px-6 shrink-0" data-testid="player-topbar">
            <div className="flex items-center gap-3 min-w-0">
                <button
                    type="button"
                    onClick={onExit}
                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-secondary/60 hover:bg-secondary text-foreground text-xs font-semibold transition-colors"
                    data-testid="player-exit-btn"
                >
                    <ArrowLeft size={12} weight="bold" /> Exit
                </button>
                <div className="w-px h-5 bg-border" />
                <div className="min-w-0">
                    <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground">
                        ICPP · Phase {phase.index} · {phaseSub}
                    </div>
                    <div className="text-xs font-bold text-foreground truncate" data-testid="player-module-name">
                        Module {module.index} — {module.title}
                    </div>
                </div>
            </div>

            <div className="flex items-center gap-3 shrink-0">
                <span className="text-[11px] text-muted-foreground hidden md:inline">Progress</span>
                <div className="w-32 h-1.5 bg-secondary rounded-full overflow-hidden">
                    <div
                        className="h-full rounded-full bg-primary transition-all duration-500"
                        style={{ width: `${modulePct}%` }}
                        data-testid="player-progress-fill"
                    />
                </div>
                <span className="text-xs font-bold text-primary tabular-nums w-9 text-right">{modulePct}%</span>
            </div>
        </div>
    );
}


/* ─────────────────────────────────────────────────────────────────────
   LEFT RAIL — MODULE SIDEBAR
   ───────────────────────────────────────────────────────────────────── */

function ModuleSidebar({ phase, modules, currentModuleId, progress, PhaseIcon, onSelect }) {
    const totalMinutes = modules.reduce((acc, m) => acc + (m.durations?.[progress?.track] || 0), 0);
    const hoursLabel = totalMinutes >= 60 ? `~${Math.round(totalMinutes / 60 * 10) / 10} hrs` : `~${totalMinutes} min`;

    return (
        <aside className="w-72 bg-white border-r border-border flex flex-col overflow-hidden shrink-0 hidden md:flex" data-testid="player-sidebar">
            <div className="p-5 border-b border-border">
                <Badge className="bg-primary/10 text-primary border border-primary/20 text-[10px] uppercase tracking-[0.08em] font-bold px-2.5 py-0.5 mb-2">
                    Phase {phase.index} of 4
                </Badge>
                <div className="flex items-center gap-2 mb-1">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                        <PhaseIcon size={16} weight="duotone" className="text-primary" />
                    </div>
                    <h3 className="font-display text-sm font-bold text-foreground leading-tight">{phase.label}</h3>
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">
                    {modules.length} module{modules.length === 1 ? "" : "s"} · {hoursLabel}
                </p>
            </div>

            <div className="flex-1 overflow-y-auto py-2" data-testid="player-sidebar-module-list">
                {modules.map((m) => {
                    const score = progress?.quiz_scores?.[m.id];
                    const passed = !!score?.passed;
                    const priorModules = modules.slice(0, m.index - 1);
                    const priorAllPassed = priorModules.every((pm) => !!progress?.quiz_scores?.[pm.id]?.passed);
                    const isCurrent = m.id === currentModuleId;
                    const isLocked = !isCurrent && !passed && !priorAllPassed;

                    return (
                        <button
                            type="button"
                            key={m.id}
                            onClick={isLocked ? undefined : () => onSelect(m)}
                            disabled={isLocked}
                            className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors border-l-2 ${
                                isCurrent
                                    ? "bg-primary/5 border-primary"
                                    : passed
                                        ? "border-transparent hover:bg-secondary/40 cursor-pointer"
                                        : isLocked
                                            ? "border-transparent opacity-50 cursor-not-allowed"
                                            : "border-transparent hover:bg-secondary/40 cursor-pointer"
                            }`}
                            data-testid={`player-sidebar-module-${m.id}`}
                        >
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 text-[11px] font-bold ${
                                passed
                                    ? "bg-emerald-500 text-white"
                                    : isCurrent
                                        ? "bg-primary text-white"
                                        : "bg-secondary text-muted-foreground border border-border"
                            }`}>
                                {passed ? <CheckCircle size={14} weight="fill" /> : m.index}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className={`text-xs font-semibold truncate ${isCurrent ? "text-primary" : "text-foreground"}`}>
                                    {m.title}
                                </div>
                                <div className="text-[10px] text-muted-foreground flex items-center gap-2 mt-0.5">
                                    <Clock size={8} weight="duotone" />
                                    {m.durations?.[progress?.track]} min
                                    {passed && (
                                        <span className="text-emerald-700 font-bold">
                                            · {Math.round((score.best_score || 0) * 100)}%
                                        </span>
                                    )}
                                </div>
                            </div>
                            {isLocked && <Lock size={12} weight="duotone" className="text-muted-foreground shrink-0" />}
                        </button>
                    );
                })}
            </div>
        </aside>
    );
}


/* ─────────────────────────────────────────────────────────────────────
   CONTENT SCREEN — slides with progress bar + footer
   ───────────────────────────────────────────────────────────────────── */

function ContentScreen({ slides, currentSlide, onGoTo, onNext, onPrev, onStartQuiz, hasQuiz }) {
    const slidePct = slides.length > 0 ? Math.round(((currentSlide + 1) / slides.length) * 100) : 0;
    const isLast = currentSlide === slides.length - 1;
    const slide = slides[currentSlide];
    if (!slide) return null;

    return (
        <div className="flex-1 flex flex-col overflow-hidden">
            {/* Slide progress bar */}
            <div className="h-1 bg-primary/10 shrink-0">
                <div
                    className="h-full bg-primary transition-all duration-400"
                    style={{ width: `${slidePct}%` }}
                    data-testid="player-slide-progress"
                />
            </div>

            {/* Slide body */}
            <div className="flex-1 overflow-y-auto" data-testid="player-slide-area">
                <div className="max-w-4xl mx-auto px-6 md:px-12 py-10 md:py-14">
                    <SlideRenderer slide={slide} key={currentSlide} />
                </div>
            </div>

            {/* Footer nav */}
            <div className="bg-white border-t border-border px-6 md:px-12 py-3.5 flex items-center justify-between shrink-0" data-testid="player-slide-footer">
                <div className="flex items-center gap-3">
                    <div className="flex gap-1.5">
                        {slides.map((_, i) => {
                            const isDone = i < currentSlide;
                            const isActive = i === currentSlide;
                            return (
                                <button
                                    type="button"
                                    key={i}
                                    onClick={() => onGoTo(i)}
                                    aria-label={`Go to slide ${i + 1}`}
                                    className={`transition-all rounded-full ${
                                        isActive
                                            ? "w-5 h-1.5 bg-primary"
                                            : isDone
                                                ? "w-1.5 h-1.5 bg-primary/50"
                                                : "w-1.5 h-1.5 bg-secondary"
                                    }`}
                                    data-testid={`player-slide-dot-${i}`}
                                />
                            );
                        })}
                    </div>
                    <span className="text-xs text-muted-foreground tabular-nums" data-testid="player-slide-counter">
                        Slide {currentSlide + 1} of {slides.length}
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    {currentSlide > 0 && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={onPrev}
                            className="h-9"
                            data-testid="player-slide-prev"
                        >
                            <ArrowLeft size={13} weight="bold" className="mr-1" /> Back
                        </Button>
                    )}
                    {!isLast && (
                        <Button
                            size="sm"
                            onClick={onNext}
                            className="h-9 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
                            data-testid="player-slide-next"
                        >
                            Next <ArrowRight size={13} weight="bold" className="ml-1" />
                        </Button>
                    )}
                    {isLast && hasQuiz && (
                        <Button
                            size="sm"
                            onClick={onStartQuiz}
                            className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
                            data-testid="player-start-quiz"
                        >
                            <Target size={13} weight="fill" className="mr-1.5" />
                            Start Quiz
                        </Button>
                    )}
                    {isLast && !hasQuiz && (
                        <Button
                            size="sm"
                            onClick={onStartQuiz}
                            variant="outline"
                            className="h-9"
                            disabled
                        >
                            No quiz available
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}


/* ─────────────────────────────────────────────────────────────────────
   SLIDE RENDERER — dispatches on slide.type
   ───────────────────────────────────────────────────────────────────── */

function SlideRenderer({ slide }) {
    return (
        <div className="flex flex-col gap-6 animate-fadeInUp" data-testid={`player-slide-${slide.type}`}>
            {slide.tag && (
                <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 text-primary text-[11px] font-bold uppercase tracking-[0.1em] px-3 py-1.5 rounded-full w-fit">
                    {slide.tagIcon && <slide.tagIcon size={12} weight="duotone" />}
                    {slide.tag}
                </div>
            )}
            <h2 className="font-display text-2xl sm:text-3xl md:text-[32px] font-bold tracking-tight text-foreground leading-tight" data-testid="player-slide-heading">
                {slide.heading}
            </h2>
            {slide.description && (
                <p className="text-[15px] text-foreground/80 leading-relaxed max-w-3xl">{slide.description}</p>
            )}

            {slide.type === "intro" && slide.visual && (
                <div className="bg-primary/5 border border-primary/15 rounded-2xl p-8 md:p-12 flex items-center justify-center min-h-[200px]">
                    <div className="text-center">
                        {slide.visual.icon && <slide.visual.icon size={72} weight="duotone" className="text-primary mx-auto mb-3" />}
                        <div className="text-sm font-semibold text-foreground/70 max-w-md">
                            {slide.visual.caption}
                        </div>
                    </div>
                </div>
            )}

            {slide.type === "intro" && slide.stats && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {slide.stats.map((s, i) => (
                        <div key={i} className="bg-white border border-border rounded-xl p-4 bento-shadow">
                            <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground">{s.label}</div>
                            <div className="font-display text-xl font-bold text-foreground mt-1">{s.value}</div>
                        </div>
                    ))}
                </div>
            )}

            {slide.type === "objectives" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {slide.items.map((it, i) => (
                        <div key={i} className="bg-white border border-border rounded-xl p-5 bento-shadow flex gap-3">
                            <div className="w-8 h-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                                <Target size={16} weight="duotone" className="text-primary" />
                            </div>
                            <p className="text-sm text-foreground leading-relaxed">{it}</p>
                        </div>
                    ))}
                </div>
            )}

            {slide.type === "topics" && (
                <div className="flex flex-col gap-2.5">
                    {slide.items.map((it, i) => (
                        <div key={i} className="bg-white border border-border rounded-xl p-4 flex items-start gap-4 bento-shadow">
                            <div className="w-8 h-8 rounded-full bg-primary text-white text-xs font-bold flex items-center justify-center shrink-0">
                                {i + 1}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-[13px] font-semibold text-foreground leading-snug">{it.label}</div>
                                {it.depth === "brief" && (
                                    <Badge variant="outline" className="text-[9px] uppercase tracking-wider mt-1.5">
                                        Brief overview
                                    </Badge>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {slide.type === "topic-teach" && (
                <>
                    {(slide.visualKind && VISUALS_BY_KIND[slide.visualKind]) ? (
                        <div className="bg-primary/5 border border-primary/15 rounded-2xl px-6 md:px-8 py-8 md:py-10 flex items-center justify-center min-h-[180px]">
                            <div className="w-full max-w-md flex flex-col items-center gap-3">
                                <AcademyVisual kind={slide.visualKind} className="text-primary" />
                                {slide.visualHint && (
                                    <div className="text-xs font-semibold text-foreground/60 max-w-md leading-relaxed text-center">
                                        {slide.visualHint}
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : slide.visualHint ? (
                        <div className="bg-primary/5 border border-primary/15 rounded-2xl p-8 md:p-10 flex items-center justify-center min-h-[180px]">
                            <div className="text-center">
                                {slide.visualIcon && <slide.visualIcon size={64} weight="duotone" className="text-primary mx-auto mb-3" />}
                                <div className="text-sm font-semibold text-foreground/70 max-w-md leading-relaxed">
                                    {slide.visualHint}
                                </div>
                            </div>
                        </div>
                    ) : null}

                    {slide.clinicalContext && (
                        <div className="bg-primary/5 border border-primary/20 rounded-2xl p-5 flex items-start gap-3">
                            <div className="w-9 h-9 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                                <Stethoscope size={18} weight="duotone" className="text-primary" />
                            </div>
                            <div>
                                <div className="font-display text-sm font-bold text-foreground mb-1 inline-flex items-center gap-1.5">
                                    <Lightbulb size={13} weight="fill" className="text-primary" />
                                    Clinical context
                                </div>
                                <div className="text-sm text-foreground/85 leading-relaxed">{slide.clinicalContext}</div>
                            </div>
                        </div>
                    )}
                </>
            )}

            {slide.type === "summary" && (
                <>
                    <div className="flex flex-col gap-2.5">
                        {slide.recap.map((r, i) => (
                            <div key={i} className="bg-white border border-border rounded-xl p-4 flex items-start gap-3 bento-shadow">
                                <CheckCircle size={20} weight="fill" className="text-emerald-500 shrink-0 mt-0.5" />
                                <div className="flex-1">
                                    <div className="text-sm font-semibold text-foreground">{r.title}</div>
                                    {r.body && <div className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{r.body}</div>}
                                </div>
                            </div>
                        ))}
                    </div>
                    {slide.quizInfo && (
                        <div className="bg-primary/5 border border-primary/20 rounded-2xl p-5 flex items-start gap-3">
                            <div className="w-9 h-9 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                                <Target size={18} weight="duotone" className="text-primary" />
                            </div>
                            <div>
                                <div className="font-display text-sm font-bold text-foreground mb-1">Quiz info</div>
                                <div className="text-xs text-foreground/80 leading-relaxed">{slide.quizInfo}</div>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}


/* ─────────────────────────────────────────────────────────────────────
   QUIZ SCREEN — one question at a time (batch-submit at end)
   ───────────────────────────────────────────────────────────────────── */

function QuizScreen({ questions, currentIdx, answers, onSelectOption, onNext, onPrev, onSubmit, submitting, onBackToContent }) {
    const q = questions[currentIdx];
    if (!q) {
        return (
            <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">
                No questions available.
            </div>
        );
    }
    const selected = answers[currentIdx];
    const isLast = currentIdx === questions.length - 1;
    const answeredCount = Object.keys(answers).length;
    const pct = Math.round(((currentIdx) / questions.length) * 100);
    const letters = ["A", "B", "C", "D", "E", "F"];

    return (
        <div className="flex-1 flex flex-col overflow-hidden" data-testid="player-quiz-screen">
            {/* Quiz header — subtle teal, no purple */}
            <div className="bg-primary/5 border-b border-primary/10 px-6 md:px-12 py-6 shrink-0">
                <div className="max-w-4xl mx-auto">
                    <div className="flex items-center justify-between mb-3">
                        <Badge className="bg-primary/10 text-primary border border-primary/20 text-[10px] uppercase tracking-[0.1em] font-bold px-2.5 py-1">
                            <Target size={11} weight="fill" className="mr-1" />
                            Knowledge Check
                        </Badge>
                        <div className="text-xs font-semibold text-muted-foreground" data-testid="player-quiz-counter">
                            Question {currentIdx + 1} of {questions.length}
                        </div>
                    </div>
                    <div className="w-full h-1.5 bg-white/60 border border-primary/10 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-primary transition-all duration-400"
                            style={{ width: `${pct}%` }}
                            data-testid="player-quiz-progress"
                        />
                    </div>
                    <h3 className="font-display text-xl md:text-2xl font-bold tracking-tight text-foreground leading-snug mt-5" data-testid="player-quiz-question">
                        {q.is_scenario && (
                            <Badge className="mr-2 bg-amber-50 text-amber-800 border border-amber-200 align-middle text-[10px] uppercase tracking-[0.08em]">
                                Clinical scenario
                            </Badge>
                        )}
                        {q.question}
                    </h3>
                </div>
            </div>

            {/* Options */}
            <div className="flex-1 overflow-y-auto px-6 md:px-12 py-8">
                <div className="max-w-4xl mx-auto space-y-3">
                    {q.options.map((opt, oi) => {
                        const isSelected = selected === oi;
                        return (
                            <button
                                type="button"
                                key={oi}
                                onClick={() => onSelectOption(oi)}
                                className={`w-full flex items-center gap-4 p-4 md:p-5 rounded-2xl border-2 text-left transition-all ${
                                    isSelected
                                        ? "border-primary bg-primary/5 shadow-[0_2px_12px_rgba(0,124,157,0.15)]"
                                        : "border-border bg-white hover:border-primary/40 hover:bg-primary/5 hover:translate-x-1"
                                }`}
                                data-testid={`player-quiz-option-${oi}`}
                            >
                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 text-sm font-bold border-2 transition-all ${
                                    isSelected
                                        ? "bg-primary text-white border-primary"
                                        : "bg-secondary/50 text-muted-foreground border-border"
                                }`}>
                                    {letters[oi]}
                                </div>
                                <span className="text-[15px] text-foreground font-medium flex-1 leading-relaxed">{opt}</span>
                                {isSelected && <CheckCircle size={20} weight="fill" className="text-primary shrink-0" />}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Footer */}
            <div className="bg-white border-t border-border px-6 md:px-12 py-3.5 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        onClick={onBackToContent}
                        className="text-xs font-semibold text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                        data-testid="player-quiz-back-to-content"
                    >
                        <ArrowLeft size={12} weight="bold" /> Back to lesson
                    </button>
                    <span className="text-xs text-muted-foreground">
                        Answered: <strong className="text-foreground tabular-nums">{answeredCount} / {questions.length}</strong>
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    {currentIdx > 0 && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={onPrev}
                            className="h-9"
                            data-testid="player-quiz-prev"
                        >
                            <ArrowLeft size={13} weight="bold" className="mr-1" /> Previous
                        </Button>
                    )}
                    {!isLast ? (
                        <Button
                            size="sm"
                            onClick={onNext}
                            disabled={selected === undefined}
                            className="h-9 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
                            data-testid="player-quiz-next"
                        >
                            Next question <ArrowRight size={13} weight="bold" className="ml-1" />
                        </Button>
                    ) : (
                        <Button
                            size="sm"
                            onClick={onSubmit}
                            disabled={submitting || answeredCount < questions.length}
                            className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
                            data-testid="player-quiz-submit"
                        >
                            {submitting ? "Grading…" : "Submit answers"}
                            {!submitting && <Target size={13} weight="fill" className="ml-1.5" />}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}


/* ─────────────────────────────────────────────────────────────────────
   SCORE SCREEN — pass/fail ring + inline per-question review
   ───────────────────────────────────────────────────────────────────── */

function ScoreScreen({ result, questions, answers, module, phase, onRetake, onReview, onNextModule, hasNextModule, onDownloadCert }) {
    const pct = Math.round((result.score || 0) * 100);
    const passed = !!result.passed;
    const correct = result.correct || 0;
    const wrong = (result.total || questions.length) - correct;
    const total = result.total || questions.length;
    const phaseNowDone = phase?.id && (result?.phase_completed || false);

    // Ring geometry
    const radius = 60;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (pct / 100) * circumference;

    return (
        <div className="flex-1 overflow-y-auto" data-testid="player-score-screen">
            <div className="max-w-3xl mx-auto px-6 md:px-12 py-10 md:py-14">
                {/* Score card */}
                <div className="bg-white rounded-3xl bento-shadow p-8 md:p-12 flex flex-col items-center gap-6 text-center">
                    <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${
                        passed ? "bg-emerald-50 border border-emerald-200" : "bg-amber-50 border border-amber-200"
                    }`}>
                        {passed
                            ? <Trophy size={36} weight="fill" className="text-emerald-600" />
                            : <ArrowsClockwise size={32} weight="duotone" className="text-amber-600" />}
                    </div>

                    <div>
                        <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground" data-testid="player-score-title">
                            {passed ? "Module complete" : "Keep practising"}
                        </h2>
                        <p className="text-sm text-muted-foreground mt-2 max-w-md" data-testid="player-score-subtitle">
                            {passed
                                ? `You scored ${pct}% and passed this module. Great work.`
                                : `You scored ${pct}%. You need ${Math.round(PASS_MARK * 100)}% to pass — review the material and try again.`}
                        </p>
                    </div>

                    {/* Ring */}
                    <div className="relative w-36 h-36" data-testid="player-score-ring">
                        <svg width="144" height="144" viewBox="0 0 144 144" style={{ transform: "rotate(-90deg)" }}>
                            <circle cx="72" cy="72" r={radius} fill="none" stroke="hsl(var(--secondary))" strokeWidth="10" />
                            <circle
                                cx="72"
                                cy="72"
                                r={radius}
                                fill="none"
                                stroke={passed ? "hsl(var(--primary))" : "#f59e0b"}
                                strokeWidth="10"
                                strokeLinecap="round"
                                strokeDasharray={circumference}
                                strokeDashoffset={offset}
                                style={{ transition: "stroke-dashoffset 1s ease" }}
                            />
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <div className="font-display text-3xl font-bold text-foreground tabular-nums">{pct}%</div>
                            <div className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground font-semibold mt-1">Score</div>
                        </div>
                    </div>

                    {/* Breakdown */}
                    <div className="grid grid-cols-3 gap-3 w-full max-w-md">
                        <ScoreStat value={correct} label="Correct" tint="emerald" />
                        <ScoreStat value={wrong} label="Incorrect" tint="red" />
                        <ScoreStat value={total} label="Total" tint="slate" />
                    </div>

                    {/* Banner */}
                    {passed ? (
                        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl px-4 py-3 flex items-start gap-3 w-full max-w-md text-left">
                            <Sparkle size={20} weight="fill" className="text-emerald-600 shrink-0 mt-0.5" />
                            <div>
                                <div className="text-sm font-bold text-emerald-900">
                                    {phaseNowDone ? "Phase complete!" : hasNextModule ? "Module unlocked" : "Ready to continue"}
                                </div>
                                <div className="text-xs text-emerald-800 mt-0.5 leading-relaxed">
                                    {phaseNowDone
                                        ? `You've finished ${phase?.label}. Download your certificate below.`
                                        : hasNextModule
                                            ? "The next module in this phase is now unlocked."
                                            : "You've completed this module. Head back to the Academy to continue."}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3 flex items-start gap-3 w-full max-w-md text-left">
                            <Warning size={20} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
                            <div>
                                <div className="text-sm font-bold text-amber-900">No worries — you can retake anytime.</div>
                                <div className="text-xs text-amber-800 mt-0.5 leading-relaxed">
                                    Review the slides and try again. There is no limit on attempts.
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex flex-wrap items-center justify-center gap-2 w-full">
                        {passed && phaseNowDone && (
                            <Button
                                onClick={onDownloadCert}
                                className="h-10 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-5"
                                data-testid="player-score-download-cert"
                            >
                                <DownloadSimple size={14} weight="bold" className="mr-1.5" />
                                Download certificate
                            </Button>
                        )}
                        {passed && hasNextModule && (
                            <Button
                                onClick={onNextModule}
                                className="h-10 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-5"
                                data-testid="player-score-next-module"
                            >
                                Next module <ArrowRight size={13} weight="bold" className="ml-1.5" />
                            </Button>
                        )}
                        {!passed && (
                            <Button
                                onClick={onRetake}
                                className="h-10 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-5"
                                data-testid="player-score-retake"
                            >
                                <ArrowsClockwise size={13} weight="bold" className="mr-1.5" /> Retake quiz
                            </Button>
                        )}
                        <Button
                            onClick={onReview}
                            variant="outline"
                            className="h-10 px-5 font-semibold"
                            data-testid="player-score-review-slides"
                        >
                            Review slides
                        </Button>
                    </div>
                </div>

                {/* Per-question walkthrough (inline) */}
                <div className="mt-8" data-testid="player-score-review">
                    <div className="overline text-muted-foreground mb-3 inline-flex items-center gap-1.5">
                        <Books size={11} weight="duotone" className="text-primary" />
                        Answer review
                    </div>
                    <ol className="space-y-3">
                        {questions.map((q, qi) => {
                            const fb = result.feedback?.[qi];
                            const chosen = answers[qi];
                            const isCorrect = !!fb?.is_correct;
                            return (
                                <li
                                    key={qi}
                                    className={`bg-white rounded-2xl border-2 p-5 ${
                                        isCorrect ? "border-emerald-200" : "border-red-200"
                                    }`}
                                    data-testid={`player-score-q-${qi}`}
                                >
                                    <div className="flex items-start gap-3">
                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 text-xs font-bold ${
                                            isCorrect ? "bg-emerald-500 text-white" : "bg-red-500 text-white"
                                        }`}>
                                            {isCorrect ? <CheckCircle size={14} weight="fill" /> : <XCircle size={14} weight="fill" />}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-[11px] font-bold uppercase tracking-[0.08em] text-muted-foreground mb-1">
                                                Question {qi + 1} — {isCorrect ? "Correct" : "Incorrect"}
                                            </div>
                                            <div className="text-sm font-semibold text-foreground leading-snug mb-3">
                                                {q.question}
                                            </div>
                                            <div className="space-y-1.5">
                                                {q.options.map((opt, oi) => {
                                                    const isChosen = chosen === oi;
                                                    const isRight = fb?.correct_index === oi;
                                                    let cls = "border-border bg-white text-foreground/70";
                                                    if (isRight) cls = "border-emerald-300 bg-emerald-50 text-emerald-900 font-semibold";
                                                    else if (isChosen) cls = "border-red-300 bg-red-50 text-red-900 font-semibold";
                                                    return (
                                                        <div key={oi} className={`flex items-center gap-2.5 px-3 py-2 rounded-lg border text-xs ${cls}`}>
                                                            <div className="w-5 h-5 rounded flex items-center justify-center bg-white border border-current/20 text-[10px] font-bold shrink-0">
                                                                {["A","B","C","D","E","F"][oi]}
                                                            </div>
                                                            <span className="flex-1">{opt}</span>
                                                            {isRight && <CheckCircle size={13} weight="fill" className="text-emerald-600 shrink-0" />}
                                                            {isChosen && !isRight && <XCircle size={13} weight="fill" className="text-red-600 shrink-0" />}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            {fb?.rationale && (
                                                <div className="mt-3 bg-primary/5 border border-primary/15 rounded-lg px-3 py-2 text-xs text-foreground/80 leading-relaxed">
                                                    <span className="font-bold uppercase tracking-[0.08em] text-[10px] text-primary mr-2">
                                                        Why
                                                    </span>
                                                    {fb.rationale}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                    </ol>
                </div>
            </div>
        </div>
    );
}


function ScoreStat({ value, label, tint }) {
    const cls =
        tint === "emerald" ? "text-emerald-700 bg-emerald-50 border-emerald-200"
        : tint === "red" ? "text-red-700 bg-red-50 border-red-200"
        : "text-foreground bg-secondary/40 border-border";
    return (
        <div className={`rounded-xl border px-3 py-3 text-center ${cls}`}>
            <div className="font-display text-2xl font-bold tabular-nums">{value}</div>
            <div className="text-[11px] mt-1 font-semibold uppercase tracking-[0.08em]">{label}</div>
        </div>
    );
}


/* ─────────────────────────────────────────────────────────────────────
   Error state
   ───────────────────────────────────────────────────────────────────── */

function PlayerError({ title, message, onExit }) {
    return (
        <div className="fixed inset-0 z-50 bg-background flex items-center justify-center p-6">
            <div className="max-w-md bg-white rounded-2xl bento-shadow p-8 text-center">
                <div className="w-12 h-12 rounded-xl bg-amber-50 border border-amber-200 flex items-center justify-center mx-auto mb-3">
                    <Warning size={24} weight="fill" className="text-amber-600" />
                </div>
                <h2 className="font-display text-lg font-bold text-foreground mb-1">{title}</h2>
                <p className="text-sm text-muted-foreground leading-relaxed">{message}</p>
                <Button onClick={onExit} className="mt-5 h-10 px-5">
                    <ArrowLeft size={13} weight="bold" className="mr-1.5" />
                    Back to Academy
                </Button>
            </div>
        </div>
    );
}


/* ─────────────────────────────────────────────────────────────────────
   Slide builder — auto-lay out objectives / topics / summary from
   existing curriculum fields.
   ───────────────────────────────────────────────────────────────────── */

function buildSlidesFromModule({ module, phase, objectives, topics, questions }) {
    const PhaseIcon = PHASE_META_BY_ID[phase.id]?.icon || Books;
    const phaseSub = PHASE_META_BY_ID[phase.id]?.subtitle || phase.label;
    const slides = [];

    // Slide 0 — Intro
    slides.push({
        type: "intro",
        tag: `Module ${module.index}`,
        tagIcon: Sparkle,
        heading: module.title,
        description: module.summary,
        visual: {
            icon: PhaseIcon,
            caption: `Part of ${phaseSub} — the ICPP ${phaseSub.toLowerCase()} phase of your certification journey.`,
        },
        stats: [
            { label: "Format", value: module.format },
            { label: "Duration", value: `${module.durations?.gp || module.durations?.ortho || "—"} min` },
            { label: "Quiz", value: questions.length > 0 ? `${questions.length} questions` : "None" },
        ],
    });

    // Slide 1 — Learning Objectives
    if (objectives.length > 0) {
        slides.push({
            type: "objectives",
            tag: "Learning objectives",
            tagIcon: Target,
            heading: "What you'll learn",
            description: "By the end of this module you'll be able to confidently apply the following in your practice.",
            items: objectives,
        });
    }

    // Slides 2..N — Topics
    //
    // If a topic has a `body` field, it becomes its own dedicated teaching
    // slide (title + explanation + visual + clinical context). This is the
    // authored content path — Phase 1 Module 1 uses this today.
    //
    // Topics that don't have body content fall back to being grouped into a
    // "topics covered" list slide (max 6 per slide) — that keeps unauthored
    // modules from breaking the player. Depth "none" topics are dropped.
    const teachTopics = topics.filter((t) => t.body);
    const listTopics = topics.filter((t) => !t.body);

    if (teachTopics.length > 0) {
        teachTopics.forEach((topic, ti) => {
            slides.push({
                type: "topic-teach",
                tag: `Topic ${ti + 1} of ${teachTopics.length}`,
                tagIcon: Books,
                heading: topic.label,
                description: topic.body,
                visualHint: topic.visual_hint,
                visualIcon: PhaseIcon,
                visualKind: topic.visual_kind,
                clinicalContext: topic.clinical_context,
            });
        });
    }

    if (listTopics.length > 0) {
        // Chunk long lists at 6/slide so the fallback stays readable.
        const chunkSize = 6;
        const chunks = [];
        for (let i = 0; i < listTopics.length; i += chunkSize) {
            chunks.push(listTopics.slice(i, i + chunkSize));
        }
        chunks.forEach((chunk, ci) => {
            slides.push({
                type: "topics",
                tag: teachTopics.length > 0
                    ? "Also covered"
                    : (chunks.length > 1 ? `Topics · ${ci + 1} of ${chunks.length}` : "Topics covered"),
                tagIcon: Books,
                heading: teachTopics.length > 0
                    ? "Also touched on in this module"
                    : (chunks.length > 1 ? `In this module (${ci + 1}/${chunks.length})` : "In this module"),
                description: ci === 0 && teachTopics.length === 0
                    ? "The core concepts we'll walk through together. Deeper-dive topics are marked as 'brief overview'."
                    : undefined,
                items: chunk,
            });
        });
    }

    // Final slide — Summary + quiz teaser
    slides.push({
        type: "summary",
        tag: "Ready for the quiz",
        tagIcon: Target,
        heading: "You're ready to test your knowledge",
        description: "Here's a quick recap of what we covered before you take the quiz.",
        recap: [
            ...(objectives.slice(0, 3).map((o) => ({ title: o }))),
            ...(topics.length > 0
                ? [{ title: `${topics.length} core topic${topics.length === 1 ? "" : "s"} explored`, body: "Refer back to the slides anytime — you can review them from your score screen." }]
                : []),
        ],
        quizInfo: questions.length > 0
            ? `${questions.length} question${questions.length === 1 ? "" : "s"} · Multiple choice · Pass mark: ${Math.round(PASS_MARK * 100)}% · Unlimited attempts.`
            : "This module doesn't have a quiz.",
    });

    return slides;
}
